'use client';

import { t } from 'mobx-state-tree';
import * as THREE from 'three';

import type { Instance } from 'mobx-state-tree';

import { withSetPropAction } from '@/lib/mobx/withSetPropAction';

interface IVector2Like {
  x: number;
  y: number;
}

interface IVector3Like {
  x: number;
  y: number;
  z: number;
}

interface IEulerLike {
  x: number;
  y: number;
  z: number;
  order?: string;
}

/** Model representing a Three.js Vector2 */
export const Vector2 = t.model('Vector2', {
  x: t.number,
  y: t.number
})
  .views(self => ({
    get asVector2() {
      return new THREE.Vector2(self.x, self.y);
    },
    get asObject(): IVector2Like {
      return { x: self.x, y: self.y };
    },
    get asArray(): [number, number] {
      return [self.x, self.y];
    },
    equals(other: THREE.Vector2 | IVector2Like, epsilon = 0.0001) {
      if (!other || typeof other.x !== 'number' || typeof other.y !== 'number') {
        return false;
      }
      return (Math.abs(self.x - other.x) < epsilon && Math.abs(self.y - other.y) < epsilon);
    }
  }))
  .actions(withSetPropAction)
  .actions(self => ({
    set(vector: THREE.Vector2 | IVector2Like) {
      self.x = vector.x;
      self.y = vector.y;
    },
    setWith(x: number, y: number) {
      self.x = x;
      self.y = y;
    },
    setPartial(vector: Partial<IVector2Like>) {
      if (vector.x !== undefined) self.x = vector.x;
      if (vector.y !== undefined) self.y = vector.y;
    }
  }));

/** Model representing a Three.js Vector3 */
export const Vector3 = t.model('Vector3', {
  x: t.number,
  y: t.number,
  z: t.number
})
  .views(self => ({
    get asVector3() {
      return new THREE.Vector3(self.x, self.y, self.z);
    },
    get asObject(): IVector3Like {
      return { x: self.x, y: self.y, z: self.z };
    },
    get asArray(): [number, number, number] {
      return [self.x, self.y, self.z];
    },
    equals(other: THREE.Vector3 | IVector3Like, epsilon = 0.0001) {
      if (!other || typeof other.x !== 'number' || typeof other.y !== 'number' || typeof other.z !== 'number') {
        return false;
      }
      return (Math.abs(self.x - other.x) < epsilon && Math.abs(self.y - other.y) < epsilon && Math.abs(self.z - other.z) < epsilon);
    }
  }))
  .actions(withSetPropAction)
  .actions(self => ({
    set(vector: THREE.Vector3 | IVector3Like) {
      self.x = vector.x;
      self.y = vector.y;
      self.z = vector.z;
    },
    setWith(x: number, y: number, z: number) {
      self.x = x;
      self.y = y;
      self.z = z;
    },
    setPartial(vector: Partial<IVector3Like>) {
      if (vector.x !== undefined) self.x = vector.x;
      if (vector.y !== undefined) self.y = vector.y;
      if (vector.z !== undefined) self.z = vector.z;
    }
  }));

/** Model representing a Three.js Euler */
export const Euler = t.model('Euler', {
  x: t.number,
  y: t.number,
  z: t.number,
  order: t.optional(t.string, 'XYZ')
})
  .views(self => ({
    get asEuler() {
      return new THREE.Euler(self.x, self.y, self.z, self.order as THREE.EulerOrder);
    },
    get asObject(): IEulerLike {
      return { x: self.x, y: self.y, z: self.z, order: self.order };
    },
    get asArray(): [number, number, number, string] {
      return [self.x, self.y, self.z, self.order];
    },
    equals(other: THREE.Euler | IEulerLike, epsilon = 0.0001) {
      if (!other || typeof other.x !== 'number' || typeof other.y !== 'number' || typeof other.z !== 'number') {
        return false;
      }
      const sameOrder = other.order === undefined || other.order === self.order;
      return sameOrder &&
        (Math.abs(self.x - other.x) < epsilon &&
          Math.abs(self.y - other.y) < epsilon &&
          Math.abs(self.z - other.z) < epsilon);
    }
  }))
  .actions(withSetPropAction)
  .actions(self => ({
    set(euler: THREE.Euler | IEulerLike) {
      self.x = euler.x;
      self.y = euler.y;
      self.z = euler.z;
      if (euler.order) self.order = euler.order;
    },
    setPartial(euler: Partial<IEulerLike>) {
      if (euler.x !== undefined) self.x = euler.x;
      if (euler.y !== undefined) self.y = euler.y;
      if (euler.z !== undefined) self.z = euler.z;
      if (euler.order !== undefined) self.order = euler.order;
    }
  }));

export interface IVector2 extends Instance<typeof Vector2> { }

export interface IVector3 extends Instance<typeof Vector3> { }

export interface IEuler extends Instance<typeof Euler> { }